import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { LoopOutlined } from '@mui/icons-material';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';

import { checkFraud } from '~/services/OrdersService';
import { getGiftCards } from '~/services/PromoService';
import { getTimeDifference } from '~/services/TimeService';

import { SingleOrderPayments } from './SingleOrderPayments';
import { truncateOrderId } from './utils';

interface Props {
  order: App.Order;
}

function SingleGiftCardDetails({ order }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: App.State) => state.tenant);
  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const giftCardCode = order.gift_card_items?.[0]?.code || '';

  const [loadingFraudCheck, setLoadingFraudCheck] = useState<boolean>(false);
  const [recalculatedFraudScore, setRecalculatedFraudScore] = useState<number | undefined>(undefined);
  const [recalculatedFraudDesc, setRecalculatedFraudDesc] = useState<string | undefined>(undefined);

  const handleReRunFraudCheck = async () => {
    setLoadingFraudCheck(true);
    try {
      const response = await checkFraud(order.id_orders);
      setRecalculatedFraudScore(response.score);
      setRecalculatedFraudDesc(response.fraudDesc);
    } catch (error) {
      setRecalculatedFraudScore(undefined);
      setRecalculatedFraudDesc(undefined);
      enqueueSnackbar('Error fetching recalculated fraud check', { variant: 'error' });
    }
    setLoadingFraudCheck(false);
  };

  const { data: giftCardData } = useQuery({
    queryKey: ['giftCard', giftCardCode, order.business_id],
    queryFn: async () => {
      if (!giftCardCode) {
        return null;
      }
      return await getGiftCards({
        code: giftCardCode,
        pageNum: 0,
        pageSize: 1,
        brand: tenant.brand,
      });
    },
    enabled: !!giftCardCode,
  });

  const giftCardLog = giftCardData?.result?.logs?.[0];

  const getRedemptionTimeDifference = () => {
    if (!giftCardLog?.created_at || !giftCardLog?.redeemed_at) {
      return null;
    }

    const giftCardCreatedDate = new Date(giftCardLog.created_at);
    const redemptionDate = new Date(giftCardLog.redeemed_at);

    const hoursDiff = Math.abs(redemptionDate.getTime() - giftCardCreatedDate.getTime()) / (1000 * 60 * 60);
    const includeHoursMinsSecs = hoursDiff < 25;

    return getTimeDifference(redemptionDate, giftCardCreatedDate, includeHoursMinsSecs, true);
  };

  const redemptionTimeDifference = getRedemptionTimeDifference();

  const getGiftCardStatusMessage = () => {
    if (!giftCardLog) {
      return 'No gift card log found';
    }

    if (giftCardLog.gift_card_status === 'redeemed' && redemptionTimeDifference) {
      return (
        <>
          Redeemed {redemptionTimeDifference} after purchase by{' '}
          {giftCardLog.redeemed_by === order.fk_customer_id ? (
            <>themselves</>
          ) : (
            <Link href={`/users/${giftCardLog.redeemed_by}`} target="_blank" rel="noopener noreferrer">
              another user
            </Link>
          )}
        </>
      );
    }

    if (giftCardLog.gift_card_status === 'redeemed') {
      return <>(Redeemed with no time info)</>;
    }

    if (giftCardLog.gift_card_status === 'cancelled') {
      return <>Gift card cancelled (never redeemed)</>;
    }

    const giftCardCreatedDate = giftCardLog?.created_at ? new Date(giftCardLog.created_at) : null;
    const now = new Date();
    const timeSincePurchase = giftCardCreatedDate ? getTimeDifference(now, giftCardCreatedDate, false, true) : null;
    return <>Not yet redeemed – {timeSincePurchase || 'unknown time'} since purchase</>;
  };

  const getPersonalizedMessage = () => {
    const giftCardItem = order.gift_card_items?.[0];
    if (!giftCardItem?.personalised) {
      return null;
    }

    const { to, from, message, is_blank_message } = giftCardItem.personalised;

    if (is_blank_message) {
      return <>(Blank message on card)</>;
    }

    return `To ${to || 'Unknown'}: "${message}" From ${from || 'Unknown'}`;
  };

  return (
    <Box>
      Order {truncateOrderId(order.id)}{' '}
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        gift_card
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      {order.customer_full_name} {order.customer_email}
      <br />
      <Typography variant="caption" color="text.secondary">
        {getGiftCardStatusMessage()}
      </Typography>
      {getPersonalizedMessage() && (
        <>
          <br />
          <Typography variant="caption" color="text.secondary">
            {getPersonalizedMessage()}
          </Typography>
        </>
      )}
      <br />
      <strong>Fraud Score: </strong>
      <Tooltip title={order.fraud_desc}>
        <span>{order.fraud_score}</span>
      </Tooltip>
      <Tooltip title="Re-run fraud check">
        <IconButton size="small" loading={loadingFraudCheck} onClick={handleReRunFraudCheck}>
          <LoopOutlined />
        </IconButton>
      </Tooltip>
      <br />
      {loadingFraudCheck && <span>Loading...</span>}
      {recalculatedFraudScore !== undefined && !loadingFraudCheck && (
        <Box>
          <strong>Recalculated Fraud Score: </strong>
          <Tooltip title={recalculatedFraudDesc}>
            <span>{recalculatedFraudScore}</span>
          </Tooltip>
        </Box>
      )}
      <SingleOrderPayments order={order} />
    </Box>
  );
}

export { SingleGiftCardDetails };
